import Analytics from '@withlocals/withlocals-analytics';
import { OAuth2Info } from 'aggregates/authentication/commands';

import { WlWindow } from '../../../typings/globals/cordova';
import { getScript } from '../../utils/getScript';

interface MyWindow extends WlWindow {
  gapi?: any;
  plugins?: any;
}

declare const window: MyWindow;

type GoogleUser = gapi.auth2.GoogleUser;
type AuthResponse = gapi.auth2.AuthResponse;

export function initGAPI(
  onSuccess: () => void,
  onError: (error: string) => void,
): void {
  if (!window.gapi) {
    getScript('https://apis.google.com/js/client:platform.js')
      .then(() => {
        window.gapi.load('auth2', {
          callback() {
            window.gapi.auth2
              .init({
                client_id: GOOGLE_ID,
              })
              .then(onSuccess, onError);
          },
          onerror() {
            onError('google auth2 failed to load');
          },
          timeout: 5000,
          ontimeout() {
            onError('google auth2 loading timed out');
          },
        });
      })
      .catch((e) => onError('gapi client script not loaded'));
  }
}

function googleUserToAuthResponse(
  user: GoogleUser,
  companyName?: string,
): OAuth2Info {
  const googleAuthResponse: AuthResponse = user.getAuthResponse(true);
  const authResponse: OAuth2Info = {
    companyName,
    accessToken: googleAuthResponse.access_token,
    expiresIn: googleAuthResponse.expires_in,
    email: user.getBasicProfile().getEmail(),
    userID: googleAuthResponse.id_token,
  };
  return authResponse;
}

export function signInGoogleWeb(companyName?: string): Promise<OAuth2Info> {
  if (window.gapi && window.gapi.auth2 && window.gapi.auth2.getAuthInstance()) {
    return window.gapi.auth2
      .getAuthInstance()
      .signIn()
      .then((user) => {
        return googleUserToAuthResponse(user, companyName);
      });
  }
  return Promise.reject('GAPI scripts not initiated');
}

interface GooglePlusPluginResponse {
  userId?: string;
  refreshToken?: string;
  email?: string;
  accessToken?: string;
  idToken?: string;
}

export function signInGoogleApp(companyName?: string): Promise<OAuth2Info> {
  return new Promise<OAuth2Info>((resolve, reject) => {
    window.plugins.googleplus.login(
      {},
      (obj: GooglePlusPluginResponse) => {
        const oauthInfo: OAuth2Info = {
          companyName,
          accessToken: obj.accessToken,
          refreshToken: obj.refreshToken,
          email: obj.email,
          userID: obj.idToken,
        };
        resolve(oauthInfo);
      },
      (msg) => {
        Analytics.track(
          window.previousNonAuthPageCategory ||
            window.currentPageCategory ||
            'App',
          'Google login error',
          `${msg}`,
        );
        reject(msg);
      },
    );
  });
}
